class JobSlider {
  constructor(el) {
    this.roots = document.querySelectorAll('.' + el);
    if (!this.roots.length || typeof Swiper === 'undefined') return;

    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    this.canHover = window.matchMedia('(hover: hover)').matches;
    this.spQuery = window.matchMedia('(max-width: 767px)');
    this.minLoopSlides = 4;

    // 同じ構造が3事業ぶん並ぶので、ページネーションは自分と同じ親の中から拾う
    this.items = Array.from(this.roots)
      .map((root) => {
        const slides = Array.from(root.querySelectorAll('.swiper-slide'));
        return {
          root,
          slides,
          track: root.querySelector('.swiper-wrapper'),
          pagination: root.parentElement.querySelector('.js-job-pagination'),
          count: slides.length,
          repeat: 1,
          swiper: null,
        };
      })
      .filter((item) => item.count > 0);

    this.items.forEach((item) => this.bindPaginationKeys(item));
    this.spQuery.addEventListener('change', () => this.update());
    this.update();
  }

  // type: "custom" のドットは Swiper の a11y の対象外で Enter / Space が効かない。
  // 既定のドットと操作感を揃える。要素は作り直されないので一度だけ張ればよい
  bindPaginationKeys(item) {
    if (!item.pagination) return;

    item.pagination.addEventListener('keydown', (e) => {
      const type = item.swiper && item.swiper.params.pagination.type;
      if (type !== 'custom') return;
      if (e.key !== 'Enter' && e.key !== ' ') return;

      const bullet = e.target.closest(
        '.p-business__job-works-pagination-bullet'
      );
      if (!bullet) return;

      e.preventDefault();
      bullet.click();
    });
  }

  // loop は breakpoints から切り替えられないため、SP / PC で作り直す
  update() {
    this.items.forEach((item) => {
      if (item.swiper) {
        item.swiper.destroy(true, true);
      }
      item.repeat = this.repeatCount(item);
      // 記事が2件で loop させると中央の左右に同じ記事が並んでしまうため loop をやめる
      item.bounded = this.spQuery.matches && item.count === 2;
      // 両端揃えのときは画面端に付かないよう、CSS 側で左右の余白を作る
      item.root.classList.toggle('is-bounded', item.bounded);
      this.syncClones(item);
      item.swiper = new Swiper(item.root, this.options(item));
    });
  }

  // SP は中央の1枚の左右を覗かせるデザインなので、記事が少なくても loop を成立させる。
  // 2件以下は loop させないので複製もしない
  repeatCount(item) {
    if (!this.spQuery.matches) return 1;
    if (item.count < 3 || item.count >= this.minLoopSlides) return 1;
    return Math.ceil(this.minLoopSlides / item.count);
  }

  // 必要枚数はブレイクポイントで変わるので、複製は毎回作り直す
  syncClones(item) {
    item.track
      .querySelectorAll('[data-job-slider-clone]')
      .forEach((clone) => clone.remove());

    for (let i = 1; i < item.repeat; i += 1) {
      item.slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-job-slider-clone', '');
        clone.removeAttribute('data-swiper-slide-index');
        item.track.appendChild(clone);
      });
    }
  }

  options(item) {
    const isSp = this.spQuery.matches;
    const total = item.count * item.repeat;
    const perView = isSp ? 1 : 3;
    const canSlide = total > perView;

    return {
      slidesPerView: isSp ? 'auto' : 3,
      centeredSlides: isSp,
      centeredSlidesBounds: item.bounded,
      // SP: 15 / 400 = 3.75%　PC: 25 / 800 = 3.125%
      spaceBetween: isSp ? '3.75%' : '3.125%',
      speed: this.reduceMotion ? 0 : 600,
      loop: canSlide && !item.bounded,
      loopAddBlankSlides: false,
      allowTouchMove: canSlide,
      grabCursor: canSlide && this.canHover,
      threshold: 8,
      preventClicks: true,
      preventClicksPropagation: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      pagination: this.pagination(item),
      a11y: {
        enabled: true,
        slideRole: null,
        // カードにフォーカスが移ったとき、
        // タブ移動が同じカードを巡り続けてスライダーから抜けられなくなるので切る
        scrollOnFocus: false,
        paginationBulletMessage: '{{index}}番目の施工事例へ',
      },
      on: {
        touchMove: (swiper, e) => this.limitDragToOneSlide(swiper, e),
      },
    };
  }

  // 1回の操作で1枚ぶんまでしか引っ張れないようにする
  limitDragToOneSlide(swiper, e) {
    const grid = swiper.slidesGrid;
    if (!grid || grid.length < 2) return;

    const i = swiper.activeIndex;
    const step = i < grid.length - 1 ? grid[i + 1] - grid[i] : grid[i] - grid[i - 1];
    const limit = step * 0.9;

    const point =
      e.targetTouches && e.targetTouches.length ? e.targetTouches[0] : e;
    const diff = point.pageX - swiper.touches.startX;
    if (Math.abs(diff) <= limit) return;

    swiper.touches.startX = point.pageX - (diff > 0 ? limit : -limit);
  }

  pagination(item) {
    const base = {
      el: item.pagination,
      clickable: true,
      bulletClass: 'p-business__job-works-pagination-bullet',
      bulletActiveClass: 'is-active',
    };
    if (item.repeat === 1) return base;

    // 複製したぶんはドットに出さず、記事の実数だけ並べる。
    return Object.assign({}, base, {
      type: 'custom',
      renderCustom: (swiper, current) => {
        const active = (current - 1) % item.count;
        let html = '';
        for (let i = 0; i < item.count; i += 1) {
          html +=
            `<span class="${base.bulletClass}${i === active ? ' is-active' : ''}"` +
            ` role="button" tabindex="0" aria-label="${i + 1}番目の施工事例へ"` +
            `${i === active ? ' aria-current="true"' : ''}></span>`;
        }
        return html;
      },
    });
  }
}
